import { useState, useEffect, useCallback } from "react";
import {
  ListMusic, Plus, Trash2, Edit3, Power, RefreshCw, ChevronDown, ChevronUp,
  Shuffle, ArrowDownAZ, Music, Clock, Calendar, X, Save, Radio
} from "lucide-react";
import { BACKEND_URL } from "../../context/AuthContext";

const PLAYLIST_TYPES = [
  { value: "default", label: "General Rotation", desc: "Plays regularly based on weight" },
  { value: "once_per_x_songs", label: "Every X Songs", desc: "Plays once every N songs" },
  { value: "once_per_x_minutes", label: "Every X Minutes", desc: "Plays once every N minutes" },
  { value: "once_per_hour", label: "Once Per Hour", desc: "Plays at a specific minute each hour" },
];

const ORDER_TYPES = [
  { value: "shuffle", label: "Shuffle", icon: Shuffle },
  { value: "sequential", label: "Sequential", icon: ArrowDownAZ },
  { value: "random", label: "Random", icon: Shuffle },
];

const formatDuration = (seconds) => {
  if (!seconds) return "0:00";
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
};

const TYPE_COLORS = {
  default: { bg: "bg-purple-500/15", text: "text-purple-400", border: "border-purple-500/20" },
  once_per_x_songs: { bg: "bg-blue-500/15", text: "text-blue-400", border: "border-blue-500/20" },
  once_per_x_minutes: { bg: "bg-green-500/15", text: "text-green-400", border: "border-green-500/20" },
  once_per_hour: { bg: "bg-orange-500/15", text: "text-orange-400", border: "border-orange-500/20" },
};

export const PlaylistManager = ({ authToken, currentUser }) => {
  const [playlists, setPlaylists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ text: "", type: "" });
  const [expandedId, setExpandedId] = useState(null);
  const [form, setForm] = useState({
    name: "", type: "default", order: "shuffle", is_enabled: true,
    is_jingle: false, weight: 3, play_per_songs: 5, play_per_minutes: 30,
    play_per_hour_minute: 0, include_in_requests: true, avoid_duplicates: true,
  });

  const fetchPlaylists = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/station/playlists`, {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      if (res.ok) setPlaylists(await res.json());
    } catch (err) {
      console.error("Failed to fetch playlists:", err);
    } finally {
      setLoading(false);
    }
  }, [authToken]);

  useEffect(() => {
    if (currentUser?.azuracast_station_id) fetchPlaylists();
    else setLoading(false);
  }, [fetchPlaylists, currentUser]);

  const flash = (text, type = "success") => {
    setMessage({ text, type });
    setTimeout(() => setMessage({ text: "", type: "" }), 4000);
  };

  const resetForm = () => {
    setForm({
      name: "", type: "default", order: "shuffle", is_enabled: true,
      is_jingle: false, weight: 3, play_per_songs: 5, play_per_minutes: 30,
      play_per_hour_minute: 0, include_in_requests: true, avoid_duplicates: true,
    });
    setEditingId(null);
    setShowForm(false);
  };

  const startEdit = (p) => {
    setForm({
      name: p.name, type: p.type, order: p.order, is_enabled: p.is_enabled,
      is_jingle: p.is_jingle, weight: p.weight,
      play_per_songs: p.play_per_songs || 5,
      play_per_minutes: p.play_per_minutes || 30,
      play_per_hour_minute: p.play_per_hour_minute || 0,
      include_in_requests: p.include_in_requests,
      avoid_duplicates: p.avoid_duplicates,
    });
    setEditingId(p.id);
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const url = editingId
        ? `${BACKEND_URL}/api/station/playlists/${editingId}`
        : `${BACKEND_URL}/api/station/playlists`;
      const res = await fetch(url, {
        method: editingId ? "PUT" : "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${authToken}` },
        body: JSON.stringify(form),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Failed");
      flash(editingId ? "Playlist updated!" : `"${form.name}" created!`);
      resetForm();
      fetchPlaylists();
    } catch (err) {
      flash(err.message, "error");
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = async (id) => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/station/playlists/${id}/toggle`, {
        method: "PUT",
        headers: { Authorization: `Bearer ${authToken}` },
      });
      if (res.ok) {
        setPlaylists((prev) =>
          prev.map((p) => (p.id === id ? { ...p, is_enabled: !p.is_enabled } : p))
        );
      }
    } catch (err) {
      flash("Toggle failed", "error");
    }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Delete playlist "${name}"? This cannot be undone.`)) return;
    try {
      const res = await fetch(`${BACKEND_URL}/api/station/playlists/${id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${authToken}` },
      });
      if (res.ok) {
        flash(`"${name}" deleted`);
        fetchPlaylists();
      }
    } catch (err) {
      flash("Delete failed", "error");
    }
  };

  if (!currentUser?.azuracast_station_id) {
    return (
      <div className="text-center py-12">
        <Radio className="w-12 h-12 text-white/20 mx-auto mb-3" />
        <p className="text-white/50">Connect your AzuraCast station to manage playlists</p>
      </div>
    );
  }

  const enabledCount = playlists.filter((p) => p.is_enabled).length;
  const totalTracks = playlists.reduce((sum, p) => sum + (p.num_songs || 0), 0);

  return (
    <div className="space-y-6" data-testid="playlist-manager">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-white">
            Playlists ({playlists.length})
          </h3>
          <p className="text-white/40 text-sm">
            {enabledCount} active &middot; {totalTracks} total tracks
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={fetchPlaylists}
            className="p-2 text-white/50 hover:text-white transition"
            data-testid="playlist-refresh"
          >
            <RefreshCw className={`w-5 h-5 ${loading ? "animate-spin" : ""}`} />
          </button>
          <button
            onClick={() => { resetForm(); setShowForm(true); }}
            className="bg-purple-500 hover:bg-purple-600 text-white px-4 py-2 rounded-xl text-sm font-medium transition flex items-center gap-2"
            data-testid="playlist-create-btn"
          >
            <Plus className="w-4 h-4" /> New Playlist
          </button>
        </div>
      </div>

      {/* Status Message */}
      {message.text && (
        <div className={`flex items-center gap-2 p-3 rounded-xl text-sm ${
          message.type === "error"
            ? "bg-red-500/10 border border-red-500/20 text-red-400"
            : "bg-green-500/10 border border-green-500/20 text-green-400"
        }`} data-testid="playlist-message">
          {message.text}
        </div>
      )}

      {/* Create/Edit Form */}
      {showForm && (
        <form onSubmit={handleSubmit} className="bg-white/5 border border-purple-500/30 rounded-2xl p-6 space-y-4" data-testid="playlist-form">
          <div className="flex items-center justify-between">
            <h3 className="text-white font-semibold">{editingId ? "Edit Playlist" : "Create Playlist"}</h3>
            <button type="button" onClick={resetForm} className="text-white/40 hover:text-white"><X className="w-5 h-5" /></button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-white/60 text-xs mb-1.5">Playlist Name *</label>
              <input
                type="text" required value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="w-full bg-black/40 border border-white/10 rounded-xl px-4 py-2.5 text-white text-sm focus:outline-none focus:border-purple-500/50"
                placeholder="e.g. Morning Mix"
                data-testid="playlist-name-input"
              />
            </div>
            <div>
              <label className="block text-white/60 text-xs mb-1.5">Playlist Type</label>
              <select
                value={form.type}
                onChange={(e) => setForm({ ...form, type: e.target.value })}
                className="w-full bg-black/40 border border-white/10 rounded-xl px-4 py-2.5 text-white text-sm focus:outline-none focus:border-purple-500/50"
                data-testid="playlist-type-select"
              >
                {PLAYLIST_TYPES.map((t) => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </select>
              <p className="text-white/30 text-xs mt-1">
                {PLAYLIST_TYPES.find((t) => t.value === form.type)?.desc}
              </p>
            </div>
            <div>
              <label className="block text-white/60 text-xs mb-1.5">Play Order</label>
              <div className="flex gap-2">
                {ORDER_TYPES.map((o) => (
                  <button
                    key={o.value} type="button"
                    onClick={() => setForm({ ...form, order: o.value })}
                    className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium transition ${
                      form.order === o.value
                        ? "bg-purple-500/20 border border-purple-500/40 text-purple-400"
                        : "bg-white/5 border border-white/10 text-white/40 hover:text-white"
                    }`}
                  >
                    <o.icon className="w-3.5 h-3.5" /> {o.label}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="block text-white/60 text-xs mb-1.5">
                Weight: <span className="text-purple-400">{form.weight}</span>
              </label>
              <input
                type="range" min="1" max="25" value={form.weight}
                onChange={(e) => setForm({ ...form, weight: parseInt(e.target.value) })}
                className="w-full h-2 rounded-full appearance-none cursor-pointer bg-white/10 accent-purple-500"
                data-testid="playlist-weight-slider"
              />
              <div className="flex justify-between text-white/30 text-xs mt-1">
                <span>Low</span><span>High priority</span>
              </div>
            </div>

            {/* Conditional fields based on type */}
            {form.type === "once_per_x_songs" && (
              <div>
                <label className="block text-white/60 text-xs mb-1.5">Play every X songs</label>
                <input
                  type="number" min="1" max="100" value={form.play_per_songs}
                  onChange={(e) => setForm({ ...form, play_per_songs: parseInt(e.target.value) || 5 })}
                  className="w-full bg-black/40 border border-white/10 rounded-xl px-4 py-2.5 text-white text-sm focus:outline-none focus:border-purple-500/50"
                  data-testid="playlist-per-songs"
                />
              </div>
            )}
            {form.type === "once_per_x_minutes" && (
              <div>
                <label className="block text-white/60 text-xs mb-1.5">Play every X minutes</label>
                <input
                  type="number" min="1" max="360" value={form.play_per_minutes}
                  onChange={(e) => setForm({ ...form, play_per_minutes: parseInt(e.target.value) || 30 })}
                  className="w-full bg-black/40 border border-white/10 rounded-xl px-4 py-2.5 text-white text-sm focus:outline-none focus:border-purple-500/50"
                  data-testid="playlist-per-minutes"
                />
              </div>
            )}
            {form.type === "once_per_hour" && (
              <div>
                <label className="block text-white/60 text-xs mb-1.5">Play at minute :XX of each hour</label>
                <input
                  type="number" min="0" max="59" value={form.play_per_hour_minute}
                  onChange={(e) => setForm({ ...form, play_per_hour_minute: parseInt(e.target.value) || 0 })}
                  className="w-full bg-black/40 border border-white/10 rounded-xl px-4 py-2.5 text-white text-sm focus:outline-none focus:border-purple-500/50"
                  data-testid="playlist-hour-minute"
                />
              </div>
            )}
          </div>

          {/* Toggles */}
          <div className="flex flex-wrap gap-4">
            {[
              { key: "is_enabled", label: "Enabled" },
              { key: "is_jingle", label: "Jingle playlist" },
              { key: "include_in_requests", label: "Allow requests" },
              { key: "avoid_duplicates", label: "Avoid duplicates" },
            ].map(({ key, label }) => (
              <label key={key} className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox" checked={form[key]}
                  onChange={(e) => setForm({ ...form, [key]: e.target.checked })}
                  className="w-4 h-4 rounded border-white/20 bg-black/40 text-purple-500 focus:ring-purple-500/50"
                />
                <span className="text-white/60 text-sm">{label}</span>
              </label>
            ))}
          </div>

          <div className="flex gap-3">
            <button
              type="submit" disabled={saving}
              className="bg-purple-500 hover:bg-purple-600 disabled:opacity-50 text-white px-6 py-2.5 rounded-xl text-sm font-medium transition flex items-center gap-2"
              data-testid="playlist-save-btn"
            >
              <Save className="w-4 h-4" /> {saving ? "Saving..." : editingId ? "Update" : "Create"}
            </button>
            <button
              type="button" onClick={resetForm}
              className="bg-white/10 hover:bg-white/15 text-white px-6 py-2.5 rounded-xl text-sm font-medium transition"
            >
              Cancel
            </button>
          </div>
        </form>
      )}

      {/* Playlist List */}
      {loading ? (
        <div className="text-center py-12">
          <RefreshCw className="w-8 h-8 text-purple-400 animate-spin mx-auto" />
          <p className="text-white/50 mt-2">Loading playlists...</p>
        </div>
      ) : playlists.length === 0 ? (
        <div className="text-center py-12 bg-white/5 rounded-2xl" data-testid="playlist-empty">
          <ListMusic className="w-12 h-12 text-white/20 mx-auto mb-3" />
          <p className="text-white/50">No playlists yet</p>
          <p className="text-white/30 text-sm mt-1">Create your first playlist to organize your AutoDJ rotation</p>
        </div>
      ) : (
        <div className="space-y-2" data-testid="playlist-list">
          {playlists.map((p) => {
            const tc = TYPE_COLORS[p.type] || TYPE_COLORS.default;
            const isExpanded = expandedId === p.id;
            return (
              <div
                key={p.id}
                className={`border rounded-xl transition-all ${
                  p.is_enabled ? `bg-white/5 ${tc.border}` : "bg-white/[0.02] border-white/5 opacity-60"
                }`}
                data-testid={`playlist-item-${p.id}`}
              >
                {/* Main Row */}
                <div className="flex items-center gap-3 p-4">
                  {/* Enable toggle */}
                  <button
                    onClick={() => handleToggle(p.id)}
                    className={`w-8 h-8 rounded-lg flex items-center justify-center transition ${
                      p.is_enabled
                        ? "bg-green-500/20 text-green-400 hover:bg-green-500/30"
                        : "bg-white/5 text-white/30 hover:bg-white/10"
                    }`}
                    title={p.is_enabled ? "Disable" : "Enable"}
                    data-testid={`playlist-toggle-${p.id}`}
                  >
                    <Power className="w-4 h-4" />
                  </button>

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-white font-medium text-sm truncate">{p.name}</span>
                      <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${tc.bg} ${tc.text}`}>
                        {PLAYLIST_TYPES.find((t) => t.value === p.type)?.label || p.type}
                      </span>
                      {p.is_jingle && (
                        <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-yellow-500/15 text-yellow-400">
                          Jingle
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-3 mt-0.5 text-white/40 text-xs">
                      <span className="flex items-center gap-1"><Music className="w-3 h-3" /> {p.num_songs} tracks</span>
                      <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {formatDuration(p.total_length)}</span>
                      {p.schedule_items?.length > 0 && (
                        <span className="flex items-center gap-1"><Calendar className="w-3 h-3" /> Scheduled</span>
                      )}
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-1.5">
                    <button
                      onClick={() => setExpandedId(isExpanded ? null : p.id)}
                      className="p-2 text-white/30 hover:text-white transition"
                    >
                      {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                    </button>
                    <button
                      onClick={() => startEdit(p)}
                      className="p-2 text-blue-400/60 hover:text-blue-400 transition"
                      data-testid={`playlist-edit-${p.id}`}
                    >
                      <Edit3 className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(p.id, p.name)}
                      className="p-2 text-red-400/60 hover:text-red-400 transition"
                      data-testid={`playlist-delete-${p.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                {/* Expanded Details */}
                {isExpanded && (
                  <div className="px-4 pb-4 pt-0 border-t border-white/5">
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-3">
                      <div className="bg-black/30 rounded-lg p-3">
                        <p className="text-white/40 text-xs">Order</p>
                        <p className="text-white text-sm capitalize">{p.order}</p>
                      </div>
                      <div className="bg-black/30 rounded-lg p-3">
                        <p className="text-white/40 text-xs">Weight</p>
                        <p className="text-white text-sm">{p.weight}</p>
                      </div>
                      <div className="bg-black/30 rounded-lg p-3">
                        <p className="text-white/40 text-xs">Requests</p>
                        <p className="text-white text-sm">{p.include_in_requests ? "Allowed" : "Disabled"}</p>
                      </div>
                      <div className="bg-black/30 rounded-lg p-3">
                        <p className="text-white/40 text-xs">Duplicates</p>
                        <p className="text-white text-sm">{p.avoid_duplicates ? "Avoided" : "Allowed"}</p>
                      </div>
                      {p.type === "once_per_x_songs" && (
                        <div className="bg-black/30 rounded-lg p-3">
                          <p className="text-white/40 text-xs">Every X songs</p>
                          <p className="text-white text-sm">{p.play_per_songs}</p>
                        </div>
                      )}
                      {p.type === "once_per_x_minutes" && (
                        <div className="bg-black/30 rounded-lg p-3">
                          <p className="text-white/40 text-xs">Every X min</p>
                          <p className="text-white text-sm">{p.play_per_minutes}</p>
                        </div>
                      )}
                      {p.type === "once_per_hour" && (
                        <div className="bg-black/30 rounded-lg p-3">
                          <p className="text-white/40 text-xs">At minute</p>
                          <p className="text-white text-sm">:{String(p.play_per_hour_minute).padStart(2, "0")}</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
